<template>
  <el-row>
    <span class="body-1 cursor-pointer" @click="openVersionModal">
      {{ $t('currentVersion') + ': ' + gainImportConfiguration.version }}
    </span>
    <el-select
      v-model="localSelectedProcessType"
      class="ml-auto"
      size="small"
      style="width: 180px"
      @change="updateLocalVars"
    >
      <el-option
        v-for="option in processTypeOptions"
        :key="option.value"
        :label="$t(option.labelKey)"
        :value="option.value"
      />
    </el-select>
  </el-row>
  <el-row class="mt-2 mb-2">
    <gain-import-files-list-card
      v-if="
        localFileLogicType == FileLogicType.BooleanLogic ||
        localSelectedProcessType == ProcessType.FxRate ||
        localSelectedProcessType == ProcessType.MobilityFlagging
      "
      :disabled="!userCanWriteEngagementGainImportConfiguration || isSignedOff"
      :file-confgurations="localFileConfigurations"
      :process-type="localSelectedProcessType"
      @change-logic-type="handleChangeLogicType"
      @save="saveFileConfigurations"
    />
    <gain-import-files-sql-card
      v-else-if="localFileLogicType == FileLogicType.SqlLogic"
      :batch-files="gainBatchFiles"
      :disabled="!userCanWriteEngagementGainImportConfiguration || isSignedOff"
      :sql-confguration="localSqlConfiguration"
      :staging-files="gainStagingFiles"
      @change-logic-type="handleChangeLogicType"
      @save="saveSqlConfiguration"
      @test="testSqlConfiguration"
    />
  </el-row>
  <import-configuration-versions-modal
    v-model="showVersionModal"
    :configuration-versions="gainImportConfigurationHistory"
    :disable-restore="
      !userCanWriteEngagementGainImportConfiguration || isSignedOff
    "
    @restore="handleRestore"
    @close="showVersionModal = false"
  />
</template>

<script lang="ts">
import { ElMessage, ElRow, ElSelect, ElOption } from 'element-plus';
import { useStore } from 'vuex';
import { computed, ComputedRef, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  BatchFileSqlModel,
  ExecuteFileSqlRequest,
  FileLogicType,
  GainImportConfiguration,
  InputFileConfiguration,
  InputSqlConfiguration,
  ModelFile,
  RequirementsStatus
} from '@etp/etp-gain-bff-client/axios';
import { ProcessType } from '@etp/etp-gain-bff-client/axios';
import ImportConfigurationVersionsModal from './components/ImportConfigurationVersionsModal.vue';
import GainImportFilesListCard from './components/GainImportFilesListCard.vue';
import GainImportFilesSqlCard from './components/GainImportFilesSqlCard.vue';
import { getAllProcessTypes } from '@/views/home/data-journey/import/models/processTypeConfig';
import config from '@/config/config';

export default {
  name: 'GainImportFiles',
  components: {
    ElRow,
    ElOption,
    ElSelect,
    GainImportFilesListCard,
    GainImportFilesSqlCard,
    ImportConfigurationVersionsModal
  },
  setup() {
    const store = useStore();
    const router = useRouter();

    const showVersionModal = ref(false);
    const processTypeOptions = getAllProcessTypes(
      config.features.showMobilityFlaggingFeature
    );
    const localFileLogicType = ref(0);
    const localFileConfigurations = ref(new Array<InputFileConfiguration>());
    const localSqlConfiguration = ref({} as InputSqlConfiguration);
    const localSelectedProcessType = computed<ProcessType>({
      get: () => store.getters['import/getSelectedProcessType'],
      set: (val: ProcessType) =>
        store.dispatch('import/setSelectedProcessType', { processType: val })
    });

    const gainBatchFiles: ComputedRef<Array<BatchFileSqlModel>> = computed(
      () => store.getters['gain/getBatchFiles']
    );
    const gainImportConfiguration: ComputedRef<GainImportConfiguration> =
      computed(() => store.getters['import/getGainImportConfiguration']);
    const gainImportConfigurationHistory: ComputedRef<
      Array<GainImportConfiguration>
    > = computed(
      () => store.getters['import/getGainImportConfigurationHistory']
    );
    const gainStagingFiles: ComputedRef<Array<ModelFile>> = computed(
      () => store.getters['gain/getStagingFiles']
    );

    const openVersionModal = async () => {
      await store.dispatch('import/fetchGainImportConfigurationHistory');
      showVersionModal.value = true;
    };

    const handleChangeLogicType = (newValue: FileLogicType) => {
      localFileLogicType.value = newValue;
    };

    const handleRestore = async (configuration: GainImportConfiguration) => {
      await store.dispatch(
        'import/updateGainImportConfiguration',
        configuration
      );
      showVersionModal.value = false;
      await updateLocalVars();
    };

    const saveFileConfigurations = async (
      configurations: Array<InputFileConfiguration>
    ) => {
      await store.dispatch('import/updateGainImportConfiguration', {
        ...gainImportConfiguration.value,
        fileConfigurations: configurations,
        fileLogicType: localFileLogicType.value
      } as GainImportConfiguration);
      await updateLocalVars();
    };

    const saveSqlConfiguration = async (
      configuration: InputSqlConfiguration
    ) => {
      await store.dispatch('import/updateGainImportConfiguration', {
        ...gainImportConfiguration.value,
        inputSqlConfiguration: configuration,
        fileLogicType: localFileLogicType.value
      } as GainImportConfiguration);
      await updateLocalVars();
    };

    const testSqlConfiguration = async (request: ExecuteFileSqlRequest) => {
      let sqliteError = await store.dispatch('gain/testImportConfiguration', {
        request,
        processType: localSelectedProcessType.value
      });
      if (!sqliteError) return;
      ElMessage({
        showClose: true,
        message: sqliteError
      });
    };

    const updateLocalVars = async () => {
      await store.dispatch('import/fetchGainImportConfiguration');
      localFileLogicType.value =
        gainImportConfiguration.value.fileLogicType ??
        FileLogicType.BooleanLogic;
      localFileConfigurations.value = [
        ...(gainImportConfiguration.value.fileConfigurations ??
          new Array<InputFileConfiguration>())
      ];
      localSqlConfiguration.value = {
        ...(gainImportConfiguration.value.inputSqlConfiguration ??
          ({} as InputSqlConfiguration))
      };
    };

    const isGainEngagement: ComputedRef<boolean> = computed(
      () => store.getters['home/isGainEngagement']
    );
    const isSignedOff: ComputedRef<boolean> = computed(
      () =>
        store.getters['gain/getCurrentRequirementsStatus']
          ?.requirementsStatus === RequirementsStatus.SignedOff
    );
    const userCanWriteEngagementGainImportConfiguration: ComputedRef<boolean> =
      computed(
        () =>
          store.getters[
            'userPermissions/canWriteEngagementGainImportConfiguration'
          ]
      );

    onMounted(async () => {
      if (!isGainEngagement.value) router.push({ name: 'ImportMonitor' });
      await store.dispatch('gain/fetchCurrentRequirementsStatus');
      await store.dispatch('gain/fetchStagingFiles');
      await store.dispatch(
        'userPermissions/checkIfCanWriteEngagementGainImportConfiguration'
      );
      await updateLocalVars();
    });

    return {
      ProcessType,
      FileLogicType,
      gainBatchFiles,
      gainImportConfiguration,
      gainImportConfigurationHistory,
      gainStagingFiles,
      handleChangeLogicType,
      handleRestore,
      isSignedOff,
      localFileConfigurations,
      localFileLogicType,
      localSelectedProcessType,
      localSqlConfiguration,
      openVersionModal,
      processTypeOptions,
      saveFileConfigurations,
      saveSqlConfiguration,
      showVersionModal,
      testSqlConfiguration,
      updateLocalVars,
      userCanWriteEngagementGainImportConfiguration
    };
  }
};
</script>

<style scoped lang="scss"></style>
